import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "../../api/axios";

import {
  masterAttemptContainer,
  masterAttemptContainerActive,
  masterAttemptHeaderCard,
  masterAttemptSummaryCard,
  masterAttemptQuestionCard,
  masterAttemptSidebarCard,
  masterAttemptOptionCard,
  masterAttemptOptionSelected,
  masterAttemptOptionUnselected,
  masterAttemptModalOverlay,
  masterAttemptModalBox,
  masterAttemptNavBtnPrev,
  masterAttemptNavBtnClear,
  masterAttemptNavBtnReview,
  masterAttemptNavBtnSave,
  masterAttemptBackBtn,
} from "../../GlobalComponents/MasterCssClass";

const decodeStoredHtml = (value) => {
  if (typeof window === "undefined") return String(value || "");
  const input = String(value || "");
  const textArea = document.createElement("textarea");
  textArea.innerHTML = input;
  return textArea.value || input;
};

const toHtml = (value) => decodeStoredHtml(value).replace(/\n/g, "<br />");

const getQKey = (q, idx) => `q_idx_${idx}_${q?._id || "item"}`;

const getLatestMockSchedule = (set = {}) => {
  const historyList = Array.isArray(set.mockTestPublishedHistory) ? set.mockTestPublishedHistory : [];
  const latestPublished = [...historyList]
    .reverse()
    .find((item) => String(item?.action || "").toUpperCase() === "PUBLISHED" && item?.testDate && item?.testTime);

  return {
    testDate: latestPublished?.testDate || set.testDate,
    testTime: latestPublished?.testTime || set.testTime,
    durationMinutes: Math.max(parseInt(latestPublished?.durationMinutes || set.durationMinutes, 10) || 30, 1),
  };
};

const getMockWindow = (set = {}) => {
  const schedule = getLatestMockSchedule(set);
  if (!schedule.testDate || !schedule.testTime) return { ...schedule, startAt: null, openAt: null, expireAt: null };

  const [hours, minutes] = String(schedule.testTime || "").split(":").map(Number);
  const startAt = new Date(schedule.testDate);
  startAt.setHours(hours || 0, minutes || 0, 0, 0);
  return {
    ...schedule,
    startAt,
    openAt: new Date(startAt.getTime() - 1 * 60 * 1000),
    expireAt: new Date(startAt.getTime() + schedule.durationMinutes * 60 * 1000),
  };
};

const MockTestAttempt = () => {
  const { subjectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [markedMap, setMarkedMap] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [timeLeft, setTimeLeft] = useState(0);
  const [manualMinutes, setManualMinutes] = useState(30);
  const [isStarted, setIsStarted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const [activeSet, setActiveSet] = useState(null);
  const [attemptSaved, setAttemptSaved] = useState(false);
  const [attemptSaveFinished, setAttemptSaveFinished] = useState(false);
  const mathRef = useRef(null);
  const containerRef = useRef(null);
  const selectedSetId = searchParams.get("questionSetId") || "";

  // --- TIME WINDOW VALIDATION LOGIC (LATEST HISTORY SUPPORT) ---
  const [timeStatus, setTimeStatus] = useState("CHECKING"); 
  const [timeMessage, setTimeMessage] = useState("");

  useEffect(() => {
    if (!activeSet) {
      setTimeStatus("READY");
      return;
    }

    const windowInfo = getMockWindow(activeSet);

    if (!windowInfo.testDate || !windowInfo.testTime) {
      setTimeStatus("READY");
      return;
    }

    const checkExamWindow = () => {
      const now = new Date();
      if (now < windowInfo.openAt) {
        setTimeStatus("TOO_EARLY");
        const diffMs = windowInfo.openAt - now;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHrs = Math.floor(diffMins / 60);
        setTimeMessage(`Entry opens 5 minutes before ${windowInfo.testTime} on ${new Date(windowInfo.testDate).toLocaleDateString()}. Time remaining: ${diffHrs}h ${diffMins % 60}m`);
      } else if (now > windowInfo.expireAt) {
        setTimeStatus("EXPIRED");
        setTimeMessage("This mock test has already expired and is no longer available.");
      } else {
        setTimeStatus("READY");
        setTimeMessage("");
      }
    };

    checkExamWindow();
    const interval = setInterval(checkExamWindow, 1000);
    return () => clearInterval(interval);
  }, [activeSet]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const blockContentAction = (e) => {
      e.preventDefault();
      if (isStarted && !submitted) {
        alert("This action is disabled during the mock test.");
      }
    };
    const handleKeyDown = (e) => {
      const key = String(e.key || "").toLowerCase();
      const blockedCtrlKey = e.ctrlKey && ["a", "c", "i", "j", "p", "s", "u", "v", "x"].includes(key);
      const blockedMetaKey = e.metaKey && ["a", "c", "p", "s", "v", "x"].includes(key);
      if (
        key === "f12" ||
        key === "printscreen" ||
        e.keyCode === 123 ||
        e.keyCode === 44 ||
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) ||
        blockedCtrlKey ||
        blockedMetaKey
      ) {
        e.preventDefault();
        if (key === "printscreen" && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText("").catch(() => {});
        }
      }
    };
    const handleBeforePrint = (e) => {
      e.preventDefault();
      alert("Printing is disabled during the mock test.");
      return false;
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", blockContentAction);
    document.addEventListener("cut", blockContentAction);
    document.addEventListener("paste", blockContentAction);
    document.addEventListener("selectstart", blockContentAction);
    document.addEventListener("dragstart", blockContentAction);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("beforeprint", handleBeforePrint);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", blockContentAction);
      document.removeEventListener("cut", blockContentAction);
      document.removeEventListener("paste", blockContentAction);
      document.removeEventListener("selectstart", blockContentAction);
      document.removeEventListener("dragstart", blockContentAction);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, [isStarted, submitted]);

  useEffect(() => {
    if (isStarted && !submitted) {
      window.history.pushState(null, null, window.location.href);
      const handleBackButton = () => {
        window.history.pushState(null, null, window.location.href);
        alert("Back button is disabled! Use the 'Submit Test' button to finish.");
      };
      window.addEventListener("popstate", handleBackButton);
      return () => window.removeEventListener("popstate", handleBackButton);
    }
  }, [isStarted, submitted]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isStarted && !submitted) {
        alert("Warning: Tab switching is not allowed!");
      }
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isStarted && !submitted) {
        alert("Warning: You exited Fullscreen! Please stay in fullscreen to complete the test.");
      }
    };
    const handleWindowBlur = () => {
      if (isStarted && !submitted) {
        alert("Warning: Leaving the test window is not allowed.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isStarted, submitted]);

  const typesetMath = (root) => {
    if (!root) return;
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      setTimeout(() => typesetMath(root), 200);
      return;
    }
    if (window.MathJax.typesetClear) window.MathJax.typesetClear([root]);
    window.MathJax.typesetPromise([root]);
  };

  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.set("subjectId", subjectId);
        params.set("type", "MOCK");
        if (selectedSetId) params.set("questionSetId", selectedSetId);
        const res = await axios.get(`/master/mock-test/attempt-questions?${params.toString()}`);
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const resolvedSet = res.data?.set || null;

        setQuestions(rows); 
        setActiveSet(resolvedSet);
        setAnswers({});
        setSavedMap({});
        setMarkedMap({});
        setCurrentIndex(0);
        setSubmitted(false);
        setIsStarted(false);
        setAttemptSaved(false);
        setAttemptSaveFinished(false);
        setManualMinutes(Math.max(parseInt(getLatestMockSchedule(resolvedSet).durationMinutes, 10) || 30, 1));

        const resolvedSetId = String(resolvedSet?.questionSetId || "");
        if (resolvedSetId && resolvedSetId !== selectedSetId) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set("questionSetId", resolvedSetId);
          setSearchParams(nextParams, { replace: true });
        }
      } catch (err) {
        setError(err?.response?.data?.message || "FAILED TO LOAD QUESTIONS");
      } finally {
        setLoading(false);
      }
    };

    if (subjectId) loadQuestions();
  }, [subjectId, selectedSetId, searchParams, setSearchParams]);

  useEffect(() => {
    if (submitted || loading || !isStarted) return undefined;
    if (timeLeft <= 0) {
      setSubmitted(true);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      return undefined;
    }
    const timer = setInterval(() => setTimeLeft((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [submitted, loading, isStarted, timeLeft]);

  useEffect(() => {
    typesetMath(mathRef.current);
  }, [currentIndex, questions, submitted, loading, isStarted]);

  const score = useMemo(() => {
    let correct = 0; let attempted = 0; let totalMarks = 0; let obtained = 0;
    const perQuestion = {};
    questions.forEach((q, idx) => {
      const qKey = getQKey(q, idx);
      const marks = Number(q.marks || 1);
      const negative = Number(q.negativeMarks || 0);
      const answer = answers[qKey] || "";
      totalMarks += marks;
      if (answer) attempted += 1;
      if (answer === q.correctOption) {
        correct += 1; obtained += marks; perQuestion[qKey] = +marks;
      } else if (answer) {
        obtained -= negative; perQuestion[qKey] = -negative;
      } else {
        perQuestion[qKey] = 0;
      }
    });
    return { correct, attempted, total: questions.length, totalMarks, obtained, perQuestion };
  }, [answers, questions]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleStartExam = () => {
    if (!agreed) return;
    const windowInfo = getMockWindow(activeSet);
    const now = new Date();
    if (windowInfo.openAt && now < windowInfo.openAt) return;
    if (windowInfo.expireAt && now > windowInfo.expireAt) return;
    if (containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen().catch(() => {
        alert("Fullscreen was blocked! Please allow it to start the mock test.");
      });
    }
    const fixedMinutes = Math.max(parseInt(windowInfo.durationMinutes || activeSet?.durationMinutes || manualMinutes, 10) || 30, 1);
    setManualMinutes(fixedMinutes);
    const secondsUntilExpiry = windowInfo.expireAt ? Math.max(Math.ceil((windowInfo.expireAt - now) / 1000), 0) : fixedMinutes * 60;
    setTimeLeft(secondsUntilExpiry);
    setIsStarted(true);
  };

  const currentQuestion = questions[currentIndex];
  const currentQKey = currentQuestion ? getQKey(currentQuestion, currentIndex) : "";
  const selected = currentQKey ? answers[currentQKey] || "" : "";

  const renderImages = (images) => {
    const list = Array.isArray(images) ? images.filter(Boolean) : [];
    if (!list.length) return null;
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {list.map((img, idx) => (
          <div key={`${img}-${idx}`} className="h-20 w-20 overflow-hidden rounded-xl border border-slate-200 bg-white sm:h-24 sm:w-24">
            <img src={img} alt="question" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    );
  };

  const handleSaveAndNext = () => {
    if (!currentQuestion) return;
    if (selected) {
      setSavedMap((prev) => ({ ...prev, [currentQKey]: true }));
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleMarkForReview = () => {
    if (!currentQuestion) return;
    setMarkedMap((prev) => ({ ...prev, [currentQKey]: !prev[currentQKey] }));
  };

  const handleClearResponse = () => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQKey]: "" }));
    setSavedMap((prev) => ({ ...prev, [currentQKey]: false }));
    setMarkedMap((prev) => ({ ...prev, [currentQKey]: false }));
  };

  const handleCloseCompletion = () => {
    const role = String(localStorage.getItem("role") || "").toLowerCase();
    if (role === "organization") {
      navigate("/organization-dashboard", { replace: true });
      return;
    }
    navigate("/student", { replace: true });
  };

  const renderRichText = (value, className = "") => (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: toHtml(value || "---") }}
    />
  );

  useEffect(() => {
    const saveAttemptHistory = async () => {
      if (!submitted || !activeSet?.questionSetId || attemptSaveFinished) return;

      try {
        await axios.post("/master/mock-test-attempt/submit-attempt", {
          questionSetId: activeSet.questionSetId,
          totalQuestions: score.total,
          attemptedQuestions: score.attempted,
          correctAnswers: score.correct,
          wrongAnswers: Math.max(score.attempted - score.correct, 0),
          totalMarks: score.totalMarks,
          obtainedMarks: score.obtained,
          durationMinutes: manualMinutes,
          timeTakenSeconds: Math.max((manualMinutes * 60) - timeLeft, 0),
          answers: questions.map((q, idx) => {
            const qKey = getQKey(q, idx);
            const answer = answers[qKey] || "";
            const marks = Number(q.marks || 1);
            const negative = Number(q.negativeMarks || 0);
            const marksAwarded = !answer ? 0 : answer === q.correctOption ? marks : -negative;
            return {
              questionBankId: q.questionBankId || q._id,
              questionText: q.questionText,
              selectedOption: answer,
              correctOption: q.correctOption,
              isCorrect: Boolean(answer) && answer === q.correctOption,
              marksAwarded,
            };
          }),
        });
        setAttemptSaved(true);
      } catch (err) {
        console.error("Failed to save mock test attempt history", err);
      } finally {
        setAttemptSaveFinished(true);
      }
    };

    saveAttemptHistory();
  }, [submitted, activeSet, attemptSaveFinished, score, manualMinutes, timeLeft, questions, answers]);

  const getQuestionStatusClass = (q, idx) => {
    const qKey = getQKey(q, idx);
    const isCurrent = idx === currentIndex && !submitted;
    const isMarked = Boolean(markedMap[qKey]);
    const isAnswered = Boolean(answers[qKey]);
    const isSaved = Boolean(savedMap[qKey]);

    if (isMarked && isAnswered) {
      return "bg-purple-600 text-white border-purple-700 font-bold";
    }
    if (isMarked) {
      return "bg-violet-500 text-white border-violet-600 font-bold";
    }
    if (isAnswered || isSaved) {
      return "bg-emerald-500 text-white border-emerald-600 font-bold";
    }
    if (isCurrent) {
      return "bg-amber-400 text-slate-900 border-amber-500 font-bold";
    }
    return "bg-white text-slate-700 border-slate-300 font-medium";
  };
  
  if (loading) return <div className="p-6 text-sm font-semibold text-slate-500">Loading mock test...</div>;
  if (error) return <div className="p-6 text-sm font-semibold text-red-600">{error}</div>;
  if (!questions.length) return <div className="p-6 text-sm font-semibold text-slate-500">No questions found.</div>;

  if (!isStarted && !submitted) {
    const isStartTimeReached = timeStatus === "READY";

    return (
      <div className={masterAttemptModalOverlay}>
        <div className={masterAttemptModalBox}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">Mock Test Instructions</h2>
              <p className="text-[11px] font-bold text-blue-600 mt-0.5">{activeSet?.subjectName || questions[0]?.subjectName || "Assessment Module"}</p>
            </div>
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-700 uppercase tracking-wider border border-blue-100">Live Secure</span>
          </div>

          {timeMessage && (
            <div className={`mb-3 p-2.5 rounded-xl text-xs font-bold text-center ${timeStatus === 'EXPIRED' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              {timeMessage}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Questions</p>
              <p className="text-sm font-bold text-slate-800 mt-0.5">{questions.length}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Total Marks</p>
              <p className="text-sm font-bold text-slate-800 mt-0.5">{score.totalMarks}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-2.5 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Duration</p>
              <p className="text-sm font-bold text-blue-600 mt-0.5">{manualMinutes} m</p>
            </div>
          </div>

          <div className="space-y-3 mb-5">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl border border-slate-100 p-3 bg-slate-50/50 hover:bg-slate-50 transition">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!isStartTimeReached || timeStatus === 'EXPIRED'}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-[11px] leading-relaxed text-slate-600 font-medium">
                I agree to maintain full-screen mode, avoid tab switching, and adhere to secure mock test guidelines.
              </span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.history.back()}
              className={masterAttemptBackBtn}
            >
              Back
            </button>
            <button
              onClick={handleStartExam}
              disabled={!agreed || !isStartTimeReached || timeStatus === 'EXPIRED'}
              className={`flex-1 flex items-center justify-center rounded-xl py-2.5 text-xs font-bold uppercase transition ${
                (!agreed || !isStartTimeReached || timeStatus === 'EXPIRED')
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-300"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-md cursor-pointer"
              }`}
            >
              {timeStatus === 'TOO_EARLY' ? 'Waiting for Start Time' : timeStatus === 'EXPIRED' ? 'Test Expired' : 'Start Now'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`${masterAttemptContainer} ${
        isStarted && !submitted ? masterAttemptContainerActive : ""
      } ${submitted ? "overflow-y-auto" : "overflow-hidden"} select-none`} 
    >
      <div className={masterAttemptHeaderCard} ref={mathRef}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-1">
            <div>
              {/* <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{activeSet?.setName || "Mock Test"}</h1> */}
              <p className="mt-1 text-xs font-semibold text-slate-500 sm:text-sm">
                Topic: {activeSet?.topicName || activeSet?.subTopicName || questions[0]?.topicName || questions[0]?.subTopicName || questions[0]?.subjectName || "-"} | Total Q: {questions.length}
              </p>
            </div>
          </div>

          {!submitted ? (
            <div className={`${masterAttemptSummaryCard} border-violet-200 bg-violet-50 text-violet-900`}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">Time Remaining</p>
              <p className={`text-lg font-semibold ${timeLeft < 60 ? "text-red-600 animate-pulse" : ""}`}>
                {formatTime(timeLeft)}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {!submitted ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
          <div className={masterAttemptQuestionCard}>
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="mb-3 text-sm font-semibold text-slate-900 sm:text-base">
                <span className="mr-1">{`Q${currentIndex + 1}.`}</span>
                {renderRichText(currentQuestion.questionText, "inline")}
              </div>
              {renderImages(currentQuestion.questionImages)}

              <div className="space-y-2">
                {[
                  { key: "A", val: currentQuestion.optionA },
                  { key: "B", val: currentQuestion.optionB },
                  { key: "C", val: currentQuestion.optionC },
                  { key: "D", val: currentQuestion.optionD },
                ].map((opt) => (
                  <label
                    key={`${currentQKey}-${opt.key}`}
                    className={`${masterAttemptOptionCard} ${
                      selected === opt.key ? masterAttemptOptionSelected : masterAttemptOptionUnselected
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="radio"
                        name={currentQKey}
                        value={opt.key}
                        checked={selected === opt.key}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAnswers((prev) => ({ ...prev, [currentQKey]: val }));
                          setSavedMap((prev) => ({ ...prev, [currentQKey]: Boolean(val) }));
                        }}
                        className="mt-1"
                      />
                      <div className="text-sm font-semibold text-slate-800">
                        <span className="mr-1">{`${opt.key}.`}</span>
                        {renderRichText(opt.val, "inline")}
                      </div>
                    </div>
                    {renderImages(currentQuestion.optionImages?.[opt.key])}
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 sm:flex sm:flex-wrap">
              <button type="button" onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))} className={masterAttemptNavBtnPrev}>Previous</button>
              <button type="button" onClick={handleClearResponse} className={masterAttemptNavBtnClear}>Clear Response</button>
              <button type="button" onClick={handleMarkForReview} className={masterAttemptNavBtnReview}>Mark Review</button>
              <button type="button" onClick={handleSaveAndNext} className={masterAttemptNavBtnSave}>Save & Next</button>
            </div>
          </div>

          <div className={masterAttemptSidebarCard}>
            <p className="mb-1 text-sm font-semibold text-slate-900">Questions ({questions.length})</p>
            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid grid-cols-6 gap-2 sm:grid-cols-7 xl:grid-cols-5">
                {questions.map((q, idx) => {
                  const qKey = getQKey(q, idx);
                  return (
                    <button
                      key={qKey}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-8 w-8 rounded-md border text-sm transition-all cursor-pointer ${getQuestionStatusClass(q, idx)}`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-[11px] font-bold text-amber-800">
              Test will submit automatically when time expires.
            </div>
          </div>
        </div>
      ) : (
        <div className={masterAttemptModalOverlay}>
          <div className={masterAttemptModalBox}>
            <div className="text-center">
             <h2 className="text-lg font-extrabold text-slate-900">Thank you for giving the exam</h2>
              <p className="mt-2 text-xs font-semibold text-slate-600">
                {attemptSaved
                  ? "Your mock test has been submitted successfully. Your result will be published as soon as possible."
                  : attemptSaveFinished
                  ? "The exam has ended. Your response could not be confirmed, please contact the administrator."
                  : "Submitting your mock test response..."}
              </p>
              <button
                type="button"
                onClick={handleCloseCompletion}
                disabled={!attemptSaveFinished}
                className={`mt-5 w-full rounded-xl px-4 py-2.5 text-xs font-bold uppercase text-white ${
                  attemptSaveFinished
                    ? "bg-blue-600 hover:bg-blue-700 cursor-pointer"
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                {attemptSaveFinished ? "Close" : "Saving..."}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MockTestAttempt;
